import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useCommandHandler, useWhenContext } from "@/lib/shortcuts/solid";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import type { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import type { useTheme } from "@/providers/theme-provider";
import type { useLocation, useNavigate } from "@tanstack/solid-router";

interface AppShortcutsOptions {
  searchPalette: ReturnType<typeof useSearchPalette>;
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
  theme: ReturnType<typeof useTheme>;
  t: (message: string | MessageDescriptor) => string;
  listDialogOpen: () => boolean;
  choiceDialogOpen: () => boolean;
  groupDialogOpen: () => boolean;
  shareDialogOpen: () => boolean;
  shortcutsOpen: () => boolean;
  setShortcutsOpen: (open: boolean) => void;
  setIsSidebarOpen: (f: (prev: boolean) => boolean) => void;
  openNewListDialog: () => void;
  openNewGroupDialog: () => void;
  navigateList: (isNext: boolean) => void;
}

export function useAppShortcuts(options: AppShortcutsOptions) {
  // ── Shortcuts: push reactive context flags so the engine can evaluate
  // when-clauses. `dialog.open` is owned by the Dialog primitive / shortcuts
  // modal (the single comprehensive source via the interaction context), so it
  // is intentionally not duplicated here.
  useWhenContext({
    "palette.open": () => options.searchPalette.isOpen(),
    "drawer.open": () => options.location().pathname.includes("/todo/"),
  });

  // ── Shortcut handlers (declarative; bindings live in lib/shortcuts/defaults.ts)
  useCommandHandler(COMMAND_IDS.PALETTE_OPEN, () => {
    options.searchPalette.toggle();
  });
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    options.setShortcutsOpen(true);
  });
  useCommandHandler(COMMAND_IDS.NEW_TASK_FOCUS, () => {
    const input = document.querySelector<HTMLInputElement>(
      `input[placeholder*="${options.t(msg`Add a task...`)}"]`,
    );
    input?.focus();
  });
  useCommandHandler(COMMAND_IDS.NEW_LIST_OPEN, () => {
    options.openNewListDialog();
  });
  useCommandHandler(COMMAND_IDS.NEW_GROUP_OPEN, () => {
    options.openNewGroupDialog();
  });

  // ── UI toggles ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    options.setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void options.theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void options.theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    options.theme.toggleBackgroundStyle();
  });
  useCommandHandler(COMMAND_IDS.GOTO_INBOX, () => {
    void options.navigate({ to: "/inbox" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_MY_DAY, () => {
    void options.navigate({ to: "/my-day" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PLANNED, () => {
    void options.navigate({ to: "/planned" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_IMPORTANT, () => {
    void options.navigate({ to: "/important" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_ALL, () => {
    void options.navigate({ to: "/all" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_DASHBOARD, () => {
    void options.navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void options.navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void options.navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void options.navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void options.navigate({ to: "/language" });
  });
  useCommandHandler(COMMAND_IDS.LIST_NEXT, () => {
    options.navigateList(true);
  });
  useCommandHandler(COMMAND_IDS.LIST_PREV, () => {
    options.navigateList(false);
  });
}
